import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const SETTINGS_SECTIONS = [
  {
    title: 'Genel Ayarlar',
    fields: [
      { key: 'site_title', label: 'Site Başlığı', type: 'text' },
      { key: 'phone', label: 'Telefon', type: 'text' },
      { key: 'email', label: 'E-posta', type: 'email' },
      { key: 'address', label: 'Adres', type: 'text', full: true },
      { key: 'working_hours', label: 'Çalışma Saatleri', type: 'text' },
    ],
  },
  {
    title: 'Sosyal Medya',
    fields: [
      { key: 'facebook', label: 'Facebook', type: 'text' },
      { key: 'instagram', label: 'Instagram', type: 'text' },
      { key: 'whatsapp', label: 'WhatsApp', type: 'text' },
    ],
  },
  {
    title: 'Teklif Sayfası',
    fields: [
      { key: 'quote_title', label: 'Başlık', type: 'text', full: true },
      { key: 'quote_description', label: 'Açıklama', type: 'textarea', full: true },
      { key: 'quote_features', label: 'Özellikler (virgül ile ayırın)', type: 'text', full: true, placeholder: 'Ücretsiz Keşif,Hızlı Geri Dönüş,Detaylı Fiyatlandırma' },
    ],
  },
  {
    title: 'Harita & Footer',
    fields: [
      { key: 'maps_embed', label: 'Harita Embed Kodu', type: 'textarea', full: true },
      { key: 'maps_link', label: 'Harita Linki', type: 'text', full: true },
      { key: 'footer_text', label: 'Footer Metni', type: 'text', full: true },
    ],
  },
  {
    title: 'Tasarım',
    fields: [
      { key: 'primary_color', label: 'Ana Renk', type: 'color' },
      { key: 'secondary_color', label: 'İkincil Renk', type: 'color' },
    ],
  },
];

export default function AdminSettings() {
  useAuth();
  const [settings, setSettings] = useState({});
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      const data = {};
      if (Array.isArray(res.data)) {
        res.data.forEach((s) => {
          data[s.key] = s.value;
        });
      } else if (typeof res.data === 'object') {
        Object.assign(data, res.data);
      }
      setSettings(data);
      if (data.logo_path) {
        setLogoPreview(getImageUrl(data.logo_path));
      }
      if (data.favicon_path) {
        setFaviconPreview(getImageUrl(data.favicon_path));
      }
    } catch (err) {
      toast.error('Ayarlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Save settings as JSON
      const settingsData = {};
      Object.entries(settings).forEach(([key, value]) => {
        if (key !== 'logo_path' && key !== 'favicon_path' && value !== undefined && value !== null) {
          settingsData[key] = value;
        }
      });
      await api.put('/settings', settingsData);

      // Upload logo separately if changed
      if (logo) {
        const logoData = new FormData();
        logoData.append('logo', logo);
        await api.post('/settings/logo', logoData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // Upload favicon separately if changed
      if (favicon) {
        const favData = new FormData();
        favData.append('favicon', favicon);
        await api.post('/settings/favicon', favData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('Ayarlar başarıyla kaydedildi');
    } catch (err) {
      toast.error('Ayarlar kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Site Ayarları</h2>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          {/* Logo Upload */}
          <div className="form-group">
            <label>Logo (Header)</label>
            <input type="file" accept="image/*" onChange={handleLogoChange} />
            {logoPreview && (
              <img src={logoPreview} alt="Logo" className="image-preview" style={{maxHeight:80,background:'#111',padding:8,borderRadius:8}} />
            )}
          </div>

          {/* Favicon Upload */}
          <div className="form-group">
            <label>Favicon (Sekme İkonu)</label>
            <input type="file" accept="image/png,image/x-icon,image/svg+xml" onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setFavicon(file);
                setFaviconPreview(URL.createObjectURL(file));
              }
            }} />
            {faviconPreview && (
              <img src={faviconPreview} alt="Favicon" className="image-preview" style={{maxHeight:48,maxWidth:48}} />
            )}
            <small style={{color:'#999',fontSize:'0.8rem',marginTop:4,display:'block'}}>
              PNG veya ICO, 32x32 veya 64x64 piksel önerilir
            </small>
          </div>

          {SETTINGS_SECTIONS.map((section) => (
            <React.Fragment key={section.title}>
              <div className="full-width" style={{ borderBottom: '2px solid #eee', paddingBottom: 5, marginBottom: 0 }}>
                <h3 style={{ margin: 0, fontSize: '1rem', color: '#1a1a2e' }}>{section.title}</h3>
              </div>
              {section.fields.map((field) => (
                <div
                  className={`form-group ${field.full ? 'full-width' : ''}`}
                  key={field.key}
                >
                  <label>{field.label}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={settings[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={field.placeholder || ''}
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={settings[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={field.placeholder || ''}
                    />
                  )}
                </div>
              ))}
            </React.Fragment>
          ))}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
